/**
 * アプリレイアウト (左サイドバー + コンテンツ)
 *
 * Design Harness プロトタイプのサイドバーナビゲーションを再現する。
 * バージョンピル (guide meta)・テーマ切替・編集権限に応じた設定リンクを持つ。
 *
 * @graph-stack creative
 * @graph-domain Marketing
 * @graph-business Design Guide の左サイドバーレイアウト。ナビ・バージョンピル・テーマ切替・権限別リンクを提供。
 * @graph-connects api [calls] guide.get によるバージョン表示
 */
import { useQuery } from '@tanstack/react-query';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { Moon, Sun } from 'lucide-react';
import { useEffect, useState, type JSX } from 'react';
import { orpc } from '../api/orpc.js';
import { ALL_BRANDS_SCOPE } from '../lib/brand-scope.js';
import { nextTheme, resolveStoredTheme, THEME_STORAGE_KEY, type Theme } from '../lib/theme.js';
import { AuthProvider, useAuth } from './auth-context.js';
import { BrandScopeProvider, useBrandScope } from './brand-scope-context.js';

/** ナビゲーション項目定義。 @graph-connects none */
const NAV_ITEMS: { to: string; label: string }[] = [
  { to: '/', label: 'Overview' },
  { to: '/brand-core', label: 'Brand Core' },
  { to: '/principles', label: 'Principles' },
  { to: '/checklist', label: 'Checklist' },
  { to: '/colors', label: 'Colors' },
  { to: '/fonts', label: 'Fonts' },
  { to: '/judgment', label: 'Judgment' },
  { to: '/ai-handoff', label: 'AI Handoff' },
  { to: '/prompts', label: 'Prompts' },
  { to: '/review-examples', label: 'Review Examples' },
  { to: '/history', label: 'History' },
];

/**
 * サイドバー本体。
 * @returns サイドバー要素
 * @graph-connects api [calls] guide.get によるバージョン表示
 */
function Sidebar(): JSX.Element {
  const auth = useAuth();
  const brandScope = useBrandScope();
  const guideQuery = useQuery(orpc.guide.get.queryOptions({ input: {} }));
  const version = guideQuery.data?.guide.meta?.version;
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const [theme, setTheme] = useState<Theme>(() =>
    resolveStoredTheme(localStorage.getItem(THEME_STORAGE_KEY)),
  );

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme]);

  return (
    <aside className="fixed inset-y-0 left-0 z-20 flex w-60 flex-col overflow-hidden border-r border-line-light bg-background">
      <div className="px-5 pb-5 pt-6">
        <div className="text-caption font-semibold uppercase tracking-label text-accent-text">
          airCloset
        </div>
        <div className="mt-1 text-sm font-bold text-foreground">Design Guide</div>
        {version !== undefined && (
          <span className="mt-2 inline-block rounded border border-line bg-surface px-1.5 py-0.5 font-mono text-micro text-muted">
            v{version}
          </span>
        )}
        {brandScope.brands.length > 0 && (
          <div className="mt-3">
            <label
              htmlFor="brand-scope-select"
              className="mb-1 block text-micro font-semibold uppercase tracking-label text-muted"
            >
              ブランド
            </label>
            <select
              id="brand-scope-select"
              className="w-full rounded-md border border-line bg-surface px-2 py-1.5 text-xs text-foreground"
              value={brandScope.scope}
              onChange={(event) => {
                brandScope.setScope(event.target.value);
              }}
            >
              <option value={ALL_BRANDS_SCOPE}>全ブランド</option>
              {brandScope.brands.map((brand) => (
                <option key={brand.id} value={brand.id}>
                  {brand.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      <nav className="flex-1 overflow-y-auto py-2">
        {NAV_ITEMS.map((item) => {
          const isActive = item.to === '/' ? pathname === '/' : pathname.startsWith(item.to);
          return (
            <Link
              key={item.to}
              to={item.to}
              className={`block border-l-2 px-5 py-2 text-body transition ${
                isActive
                  ? 'border-accent bg-accent-soft text-accent-text'
                  : 'border-transparent text-subtle hover:bg-glass hover:text-foreground'
              }`}
            >
              {item.label}
            </Link>
          );
        })}
        {auth.canManageEditors && (
          <>
            <div className="mx-5 my-2 h-px bg-line-light" />
            <Link
              to="/settings"
              className={`block border-l-2 px-5 py-2 text-body transition ${
                pathname.startsWith('/settings')
                  ? 'border-accent bg-accent-soft text-accent-text'
                  : 'border-transparent text-subtle hover:bg-glass hover:text-foreground'
              }`}
            >
              Settings
            </Link>
          </>
        )}
      </nav>
      <div className="border-t border-line-light p-4">
        {auth.email !== '' && (
          <div className="mb-2 truncate text-caption text-muted">
            {auth.email}
            {auth.canEdit && <span className="ml-1 text-accent-text">(編集可)</span>}
          </div>
        )}
        <button
          type="button"
          onClick={() => {
            setTheme(nextTheme(theme));
          }}
          className="flex w-full items-center gap-2 rounded-md border border-line bg-surface px-3 py-2 text-xs text-subtle transition hover:border-accent hover:text-foreground"
        >
          {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
          {theme === 'dark' ? 'Light mode' : 'Dark mode'}
        </button>
      </div>
    </aside>
  );
}

/**
 * ルートレイアウト。AuthProvider 配下にサイドバーとコンテンツを描画する。
 * @returns レイアウト要素
 * @graph-connects none
 */
export function RootLayout(): JSX.Element {
  return (
    <AuthProvider>
      <BrandScopeProvider>
        <div className="min-h-screen bg-background">
          <Sidebar />
          <main className="ml-60 min-h-screen">
            <div className="mx-auto max-w-4xl px-10 py-10">
              <Outlet />
            </div>
          </main>
        </div>
      </BrandScopeProvider>
    </AuthProvider>
  );
}
